/**
 * Phase 16F "Notify the health plan" task on PATIENT_HOSPITALIZED (requirements §2.3 #11):
 * records date, method and contact, then resolves the notification for everyone.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { HospitalizationNotifyMethod, NotificationTaskInput } from '../../dtos/notifications/notification.dto';

const METHODS: Array<{ value: HospitalizationNotifyMethod; label: string }> = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'FAX', label: 'Fax' },
  { value: 'PORTAL', label: 'Health plan portal' },
  { value: 'OTHER', label: 'Other' },
  { value: 'EMAIL', label: 'Email' },
];

export const CONTACT_MAX = 120;

const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export interface TaskFormErrors {
  date?: string;
  method?: string;
  contact?: string;
}

export function validateTask(input: { date: string; method: string; contact: string }, today: string = todayLocal()): TaskFormErrors {
  const errors: TaskFormErrors = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) errors.date = 'Enter the date you notified the health plan.';
  else if (input.date > today) errors.date = 'The date cannot be in the future.';
  if (!METHODS.some((m) => m.value === input.method)) errors.method = 'Choose how you notified the health plan.';
  const contact = input.contact.trim();
  if (!contact) errors.contact = 'Enter who you spoke to or where you sent it.';
  else if (contact.length > CONTACT_MAX) errors.contact = `Keep it under ${CONTACT_MAX} characters.`;
  return errors;
}

interface Props {
  notificationId: string;
  onClose: () => void;
  onSubmit: (task: NotificationTaskInput) => Promise<void>;
  onDone: () => void;
}

const HospitalizationTaskDialog: React.FC<Props> = ({ notificationId, onClose, onSubmit, onDone }) => {
  const [date, setDate] = useState(todayLocal);
  const [method, setMethod] = useState<string>('');
  const [contact, setContact] = useState('');
  const [errors, setErrors] = useState<TaskFormErrors>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const titleId = `hosp-task-${notificationId}`;

  useEffect(() => {
    firstFieldRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const found = validateTask({ date, method, contact });
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await onSubmit({ date, method: method as HospitalizationNotifyMethod, contact: contact.trim() });
      onDone();
    } catch (err) {
      setSubmitError(err instanceof Error && err.message ? err.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const input = (bad: boolean) => `w-full px-3 py-2 border rounded-lg focus:ring-2 ${bad ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-2" data-notification-dialog="open">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={submit}
        noValidate
        className="w-full max-w-md bg-white rounded-lg shadow-xl p-4 sm:p-6"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">Health plan notified</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Close">
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">Record how you told the health plan about the hospitalization. This resolves the alert for everyone.</p>

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor={`${titleId}-date`} className="block text-sm font-medium text-gray-700 mb-1">Date notified</label>
            <input
              id={`${titleId}-date`}
              ref={firstFieldRef}
              type="date"
              value={date}
              max={todayLocal()}
              onChange={(e) => setDate(e.target.value)}
              className={input(!!errors.date)}
              aria-invalid={!!errors.date}
              aria-describedby={errors.date ? `${titleId}-date-err` : undefined}
            />
            {errors.date && <p id={`${titleId}-date-err`} className="text-sm text-red-600 mt-1">{errors.date}</p>}
          </div>
          <div>
            <label htmlFor={`${titleId}-method`} className="block text-sm font-medium text-gray-700 mb-1">Method</label>
            <select
              id={`${titleId}-method`}
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className={input(!!errors.method)}
              aria-invalid={!!errors.method}
              aria-describedby={errors.method ? `${titleId}-method-err` : undefined}
            >
              <option value="">Choose…</option>
              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {errors.method && <p id={`${titleId}-method-err`} className="text-sm text-red-600 mt-1">{errors.method}</p>}
          </div>
          <div>
            <label htmlFor={`${titleId}-contact`} className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
            <input
              id={`${titleId}-contact`}
              type="text"
              value={contact}
              maxLength={CONTACT_MAX + 20}
              onChange={(e) => setContact(e.target.value)}
              className={input(!!errors.contact)}
              aria-invalid={!!errors.contact}
              aria-describedby={`${titleId}-contact-hint${errors.contact ? ` ${titleId}-contact-err` : ''}`}
            />
            <p id={`${titleId}-contact-hint`} className="text-xs text-gray-500 mt-1">Health plan department, representative or reference number. No patient details.</p>
            {errors.contact && <p id={`${titleId}-contact-err`} className="text-sm text-red-600 mt-1">{errors.contact}</p>}
          </div>
        </div>

        {submitError && <p className="mt-3 text-sm text-red-600" role="alert">{submitError}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" disabled={saving}>Cancel</button>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HospitalizationTaskDialog;
